import { operatorBrand } from '@ev/shared'
import { useState } from 'react'
import { useStrings } from '../../../i18n'
import { useCardStore, useChargeCards, useHasBuiltInEdits } from '../../../state/cardStore'
import { type ChargeCard, isChargeCardActive, toggleChargeCard } from '../../../state/chargeCards'
import { hasPublishedResults, type MapResults, operatorMatchCount } from '../../../state/results'
import { selectFilters, selectResults, useAppStore } from '../../../state/store'
import { ChargeCardEditor } from './ChargeCardEditor'

/**
 * The charge cards, above the operator list they are a shortcut for.
 *
 * Each card is one toggle button rather than a checkbox, because holding it is
 * not a seventh facet - it writes the same `operators` filter the checkboxes
 * below write, and `aria-pressed` says so without implying a separate value the
 * form carries. Several can be pressed at once.
 *
 * The site count is the sum of the per-operator counts the map published, which
 * are tallied with the operator facet excluded. So the number reads as "sites
 * this card reaches under the rest of the filters", and it does not collapse to
 * the current selection the moment the card is held.
 *
 * The pencil is a sibling of the toggle, not a child of it: a button inside a
 * button is invalid, and a pointer or a screen reader would have no way to say
 * which of the two was meant. Both sit in the same list item, so the pencil
 * stays with its card however the grid wraps.
 */

const cardSiteCount = (results: MapResults, card: ChargeCard): number =>
  card.operators.reduce((total, slug) => total + operatorMatchCount(results, slug), 0)

/** `null` while closed; `{ card: null }` while defining a new one. */
type EditorTarget = { readonly card: ChargeCard | null } | null

export const ChargeCards = () => {
  const strings = useStrings()
  const filters = useAppStore(selectFilters)
  const results = useAppStore(selectResults)
  const setFilters = useAppStore((state) => state.setFilters)

  const cards = useChargeCards()
  const restoreDefaults = useCardStore((store) => store.restoreDefaults)
  const edited = useHasBuiltInEdits()

  const [editor, setEditor] = useState<EditorTarget>(null)

  const published = hasPublishedResults(results)

  return (
    <fieldset className="facet charge-cards">
      <legend className="facet__legend">{strings.filters.chargeCards}</legend>

      {/* Level with the legend, because that is the thing it adds to. */}
      <button
        className="charge-cards__add"
        type="button"
        title={strings.filters.chargeCardAdd}
        aria-label={strings.filters.chargeCardAdd}
        onClick={() => setEditor({ card: null })}
      >
        <span aria-hidden="true">+</span>
      </button>

      <p className="charge-cards__hint">{strings.filters.chargeCardHint}</p>

      <ul className="charge-cards__list">
        {cards.map((card) => {
          const active = isChargeCardActive(filters, card)
          return (
            <li key={card.id} className="charge-cards__item">
              <button
                className="charge-card"
                type="button"
                aria-pressed={active}
                onClick={() => setFilters(toggleChargeCard(filters, card, cards))}
              >
                <span className="charge-card__swatches" aria-hidden="true">
                  {card.operators.map((slug) => (
                    <span
                      key={slug}
                      className="charge-card__swatch"
                      style={{ backgroundColor: operatorBrand(slug).color }}
                    />
                  ))}
                </span>

                <span className="charge-card__name">{card.name}</span>

                <span className="charge-card__meta">
                  {strings.filters.chargeCardOperators(card.operators.length)}
                  {!published ? null : (
                    <>
                      {' · '}
                      {strings.filters.matchingSites(cardSiteCount(results, card))}
                    </>
                  )}
                </span>
              </button>

              <button
                className="charge-card__edit"
                type="button"
                title={strings.filters.chargeCardEdit(card.name)}
                aria-label={strings.filters.chargeCardEdit(card.name)}
                onClick={() => setEditor({ card })}
              >
                <span aria-hidden="true">✎</span>
              </button>
            </li>
          )
        })}
      </ul>

      {/* Only offered once there is something to undo, so the panel does not
          carry a control for a state nobody has reached. */}
      {!edited ? null : (
        <button
          className="ev-button ev-button--quiet charge-cards__restore"
          type="button"
          onClick={restoreDefaults}
        >
          {strings.filters.chargeCardRestore}
        </button>
      )}

      {editor === null ? null : (
        <ChargeCardEditor card={editor.card} cards={cards} onClose={() => setEditor(null)} />
      )}
    </fieldset>
  )
}
